import { useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  RotateCcw,
} from "lucide-react";
import type { GameRuntime } from "./runtime";
import { StageImage } from "./StageImage";
import { imageOf } from "./states";
import * as tree from "./tree";
/** Id of the scene layer an element on the play stage belongs to. */
const layerAt = (target: EventTarget) =>
  (target as Element).closest?.("[data-game-object]")?.getAttribute(
    "data-game-object",
  ) ?? null;
type Props = {
  runtime: GameRuntime;
  entities: { id: string; imageId: string | null }[];
  /** Where the stage loads a picture from: the project, or a shared link. */
  assetUrl: (id: string) => string;
  onRestart: () => void;
  /** Called after every frame, for panels that show live values. */
  onFrame?: () => void;
};
// The running game: used by the scene editor and by a shared prototype link.
export function GamePlayer({
  runtime,
  entities,
  assetUrl,
  onRestart,
  onFrame,
}: Props) {
  const room = useRef<HTMLDivElement>(null),
    keys = useRef(new Set<string>()),
    [frame, setFrame] = useState(0),
    frameCallback = useRef(onFrame);
  frameCallback.current = onFrame;
  useEffect(() => {
    let handle = 0,
      last = performance.now();
    const pressed = keys.current;
    const tick = (t: number) => {
      runtime.step((t - last) / 1000, pressed);
      last = t;
      setFrame((v) => v + 1);
      frameCallback.current?.();
      if (!runtime.result) handle = requestAnimationFrame(tick);
    };
    handle = requestAnimationFrame(tick);
    room.current?.focus();
    return () => {
      cancelAnimationFrame(handle);
      pressed.clear();
    };
  }, [runtime]);
  const content = runtime.scene.content,
    objects = runtime.objects;
  // Paint order with containers resolved: groups draw nothing themselves and
  // a hidden frame or group hides everything inside it.
  const playing = useMemo(() => {
    const byId = new Map(objects.map((o) => [o.id, o]));
    return tree
      .paintOrder(objects)
      .map((p) => p.object)
      .filter((o) => {
        if (o.kind === "group") return false;
        for (let p = o.parentId; p; p = byId.get(p)?.parentId ?? null)
          if (!byId.get(p)?.visible) return false;
        return o.visible;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [objects, frame]);
  return (
    <div className="scene-stage-wrap">
      <div
        ref={room}
        className="scene-stage is-playing"
        tabIndex={0}
        role="application"
        aria-label="Игровая сцена"
        style={{
          aspectRatio: `${content.width}/${content.height}`,
        }}
        onPointerDown={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          room.current?.focus();
          runtime.click(
            ((e.clientX - rect.left) / rect.width) *
              content.width,
            ((e.clientY - rect.top) / rect.height) *
              content.height,
          );
          runtime.pointerDown(layerAt(e.target));
          e.preventDefault();
        }}
        onPointerUp={(e) =>
          runtime.pointerUp(layerAt(e.target))
        }
        onPointerOver={(e) =>
          runtime.hover(layerAt(e.target))
        }
        onPointerLeave={() => runtime.hover(null)}
        onKeyDown={(e) => {
          if (
            [
              "KeyW",
              "KeyA",
              "KeyS",
              "KeyD",
              "ArrowUp",
              "ArrowDown",
              "ArrowLeft",
              "ArrowRight",
              "Space",
              "KeyE",
            ].includes(e.code)
          ) {
            e.preventDefault();
            keys.current.add(e.code);
            if (!e.repeat) runtime.keyDown(e.code);
          }
        }}
        onKeyUp={(e) => keys.current.delete(e.code)}
        onBlur={() => {
          keys.current.clear();
          runtime.target = null;
        }}
      >
        {playing.map((o) => {
          const image = imageOf(o, entities);
          return (
            <div
              key={o.id}
              data-game-object={o.id}
              data-object-name={o.name}
              className={`scene-object object-${o.kind} object-${o.color}`}
              style={{
                left: `${(o.x / content.width) * 100}%`,
                top: `${(o.y / content.height) * 100}%`,
                width: `${(o.width / content.width) * 100}%`,
                height: `${(o.height / content.height) * 100}%`,
                opacity: o.opacity,
                transform: o.rotation
                  ? `rotate(${o.rotation}deg)`
                  : undefined,
                filter: o.shadow
                  ? `drop-shadow(0 ${Math.round(o.shadow / 3)}px ${o.shadow}px rgb(0 0 0 / 45%))`
                  : undefined,
              }}
            >
              {o.kind === "text" ? (
                <span>{runtime.text(o.text)}</span>
              ) : o.kind === "image" && image ? (
                <StageImage
                  src={assetUrl(image)}
                  alt={o.name}
                  restart={runtime.restarts[o.id] || 0}
                />
              ) : o.player ? (
                <span className="player-dot">●</span>
              ) : o.interactable ? (
                <span>↗</span>
              ) : null}
            </div>
          );
        })}
        {runtime.result && (
          <div className="game-result" role="status">
            <div>
              <span className="eyebrow">
                {runtime.result === "win"
                  ? "СЦЕНА ПРОЙДЕНА"
                  : "ПРОВЕРКА ЗАВЕРШЕНА"}
              </span>
              <h2>{runtime.message}</h2>
              <button
                className="primary"
                onClick={onRestart}
              >
                <RotateCcw size={15} />
                Сыграть ещё
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="scene-underbar">
        <span>WASD / стрелки · Space: рывок · E: взаимодействие</span>
        <div className="game-pad">
          {(
            [
              ["ArrowLeft", ArrowLeft],
              ["ArrowUp", ArrowUp],
              ["ArrowDown", ArrowDown],
              ["ArrowRight", ArrowRight],
            ] as const
          ).map(([code, Icon]) => (
            <button
              key={code}
              className="icon-button"
              aria-label={code}
              onPointerDown={(e) => {
                e.preventDefault();
                room.current?.focus();
                keys.current.add(code);
                e.currentTarget.setPointerCapture(e.pointerId);
              }}
              onPointerUp={() => keys.current.delete(code)}
              onPointerCancel={() => keys.current.delete(code)}
            >
              <Icon size={16} />
            </button>
          ))}
          <button
            className="secondary"
            onClick={() => runtime.keyDown("Space")}
          >
            Space
          </button>
          <button
            className="secondary"
            onClick={() => runtime.keyDown("KeyE")}
          >
            E
          </button>
        </div>
      </div>
    </div>
  );
}
